import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useGetAllBlueprints, useGetBehaviorKits } from "../api/BlueprintsApi";
import {
  useCompaniesFetchAll,
  useGetCompanyWithBlueprint,
} from "../api/CompaniesApi";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import {
  buildHybridRerunPersonPayload,
  personInfoApiFromPersonFormState,
} from "../api/hybridRerunPerson";
import {
  buildHybridRerunReportBody,
  mergeHybridRerunProfileUrls,
  usePostCreateReport,
  useRerunReportRequest,
} from "../api/ReportsApi";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import Container from "../shared-components/Container";
import DropdownCombo, {
  DropdownItem,
} from "../shared-components/DropdownCombo";
import FormTwoColumnContainer from "../shared-components/FormTwoColumnContainer";
import InputField from "../shared-components/InputField";
import Divider from "../shared-components/LineDivider";
import PageHeader from "../shared-components/PageHeader";

import PersonFormAddresses, {
  AddressesDefinitions,
} from "./PersonFormAddresses";
import PersonFormAliases, { AliasesDefinitions } from "./PersonFormAliases";
import PersonFormEducation, {
  EducationDefinitions,
} from "./PersonFormEducation";
import PersonFormEmails, { EmailDefinitions } from "./PersonFormEmails";
import PersonFormErrorSummary from "./PersonFormErrorSummary";
import PersonFormJob, { JobDefinitions } from "./PersonFormJob";

import PersonFormPhones, { PhoneDefinitions } from "./PersonFormPhones";
import {
  ActionTypes,
  buildEmptyState,
  personFormReducer,
} from "./PersonFormReducer";
import PersonFormSocial, { SocialDefinitions } from "./PersonFormSocial";
import { PersonFormStateError } from "./PersonFormErrorState";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import {
  classNames,
  dropdownItemSortEval,
  formatDateForEndpoint,
  uniqueUUID,
} from "../utils";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { BehaviorKit } from "../api/Companies";
import {
  CreateReportInputApi,
  REPORT_TYPES,
  ReportsPendingReviewItem,
} from "../api/Reports";
import { Modal } from "../shared-components/Modal";
import { PersonFormReview } from "./PersonFormReview";
import { useGetPersonInfo, useUploadResume } from "../api/PersonApi";
import { useFamaFlags } from "../api/FamaFlags";
import {
  RERUN_PROFILE_PREFILL_PAGE_SIZE,
  useFetchItemsForReview,
} from "../api/ItemsApi";
import { ItemTypes } from "../api/Items";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { NameType } from "../api/Person";

const PersonForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    rerun: boolean;
    parent_report: ReportsPendingReviewItem;
  };
  const { data: personInfo, mutate: getPerson } = useGetPersonInfo();
  const [state, dispatch] = useReducer(personFormReducer, buildEmptyState());
  const [showErrors, setShowErrors] = useState(false);
  const { showResumeParse, showBehaviorKit, hybridReportRerun } =
    useFamaFlags();

  const [companyUUID, setCompanyUUID] = useState<string>();
  const [personFormError, setPersonFormError] = useState<PersonFormStateError>(
    {}
  );
  const [showModal, setShowModal] = useState(false);

  const { data: profileList, mutate: getProfiles } = useFetchItemsForReview();

  const initialBluePrintCheck = useRef<unknown>(null);
  const initialBehaviorKitChange = useRef<unknown>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const profilesPrefilledFromParentRef = useRef(false);
  const [birthDate, setBirthDate] = useState<string>("");
  const [aliases, setAliases] = useState<AliasesDefinitions[]>([]);
  const [jobs, setJobs] = useState<JobDefinitions[]>([]);
  const [emails, setEmails] = useState<EmailDefinitions[]>([]);
  const [phones, setPhones] = useState<PhoneDefinitions[]>([]);
  const [adddresses, setAddresses] = useState<AddressesDefinitions[]>([]);
  const [education, setEducation] = useState<EducationDefinitions[]>([]);
  const [socialMedia, setSocialMedia] = useState<SocialDefinitions[]>([]);
  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: isLoadingCompanies,
  } = useCompaniesFetchAll();
  const {
    mutate: getBlueprints,
    data: blueprintsList,
    isLoading: isLoadingBlueprints,
  } = useGetAllBlueprints();
  const { mutate: getBehaviorKits, data: behaviorKitList } =
    useGetBehaviorKits();

  useEffect(() => {
    if (locationState?.rerun) {
      if (locationState?.parent_report?.person_uuid) {
        getPerson(locationState?.parent_report?.person_uuid);
        getProfiles({
          report_uuid: locationState?.parent_report.report_uuid,
          item_type_name: [ItemTypes.SOCIAL_MEDIA_PROFILE],
          // Server default page_size is 10 (newest first). Incomplete prefill
          // omits older CLIENT/provided URLs; EI then drops them while recovering
          // manuals — fetch every page so the form is the full parent set.
          page_size: RERUN_PROFILE_PREFILL_PAGE_SIZE,
          fetch_all: true,
        });
      }
      setCompanyUUID(locationState.parent_report.company_uuid);
      dispatch({
        type: ActionTypes.setCompany,
        payload: locationState.parent_report.company_uuid,
      });
      dispatch({
        type: ActionTypes.setCustomID,
        payload: locationState.parent_report.client_report_id,
      });
      dispatch({
        type: ActionTypes.setBlueprint,
        payload: locationState.parent_report.blueprint_uuid,
      });
      if (showBehaviorKit) {
        dispatch({
          type: ActionTypes.setBehaviorKit,
          payload:
            locationState.parent_report.behavior_kit_uuid &&
            locationState.parent_report.behavior_kit_uuid !== ""
              ? locationState.parent_report.behavior_kit_uuid
              : "NULL",
        });
      }
    }
  }, [getPerson, getProfiles, locationState, showBehaviorKit]);

  useEffect(() => {
    if (state.company_uuid) {
      setCompanyUUID(state.company_uuid);
    }
  }, [state]);

  useEffect(() => {
    if (!profileList || !locationState?.rerun) {
      return;
    }
    if (profilesPrefilledFromParentRef.current) {
      return;
    }
    profilesPrefilledFromParentRef.current = true;

    const mergedUrls =
      mergeHybridRerunProfileUrls(profileList.items, state.profiles) ?? [];

    const seeded = mergedUrls.map((url) => ({
      url,
      key: uniqueUUID(),
    }));
    setSocialMedia(seeded);
    dispatch({ type: ActionTypes.setProfiles, payload: seeded });
  }, [profileList, locationState?.rerun, dispatch, state.profiles]);

  useEffect(() => {
    if (companyUUID) {
      getBehaviorKits(companyUUID);
    }
  }, [getBehaviorKits, companyUUID]);

  useEffect(() => {
    if (personInfo) {
      if (!personInfo.names?.[0]) return;
      setAliases([
        {
          firstName: personInfo.names[0].first_name,
          middleName: personInfo.names[0].middle_name ?? "",
          lastName: personInfo.names[0].last_name,
          key: uniqueUUID(),
          name_type: NameType.MAIN,
        },
      ]);

      setEmails(
        personInfo.emails.map((email) => ({
          email: email.address,
          key: uniqueUUID(),
        }))
      );
      setPhones(
        personInfo.phones.map((phone) => ({
          phone: phone.raw_number,
          key: uniqueUUID(),
        }))
      );
      setAddresses(
        personInfo.addresses.map((address) => ({
          street: (address.line1 || "").replace(/[,.;-]/g, ""),
          city: address.city || "",
          state: address.state || "",
          zip: address.zip || "",
          country: address.country || "",
          key: uniqueUUID(),
        }))
      );
      setEducation(
        personInfo.degrees.map((education) => ({
          degree: education.name.replace(/[,.-]/g, ""),
          institution: education.educator.replace(/[,.-]/g, ""),
          startDate: education.start_date,
          endDate: education.end_date,
          country: education.country ? education.country : "",
          key: uniqueUUID(),
        }))
      );
      setJobs(
        personInfo.jobs.map((job) => ({
          title: (job.title || "").replace(/[,.;-]/g, ""),
          company: (job.employer || "").replace(/[,.;-]/g, ""),
          startDate: job.start_date || "",
          endDate: job.end_date || "",
          country: job.country,
          key: uniqueUUID(),
        }))
      );
      setBirthDate(personInfo.birth_date ?? "");
    }
  }, [personInfo]);

  const {
    mutate: getCompaniesInfoWithBlueprint,
    data: companiesWithBlueprint,
    isLoading: isLoadingCompanyBlueprints,
  } = useGetCompanyWithBlueprint();
  const [companyItems, setCompanyItems] = useState<DropdownItem[]>([]);
  const [blueprintItems, setBlueprintItems] = useState<DropdownItem[]>([]);
  const [behaviorKitItems, setBehaviorKitItems] = useState<DropdownItem[]>([]);
  const {
    mutate: createPerson,
    data: resultCreatePerson,
    isLoading: isLoadingCreatePerson,
    error: errorCreatePerson,
    errorData: errorCreatePersonData,
  } = usePostCreateReport();
  const {
    mutateAsync: rerunReportRequest,
    isLoading: isLoadingRerun,
    error: errorRerun,
  } = useRerunReportRequest();
  const { canBulkUploadReports } = usePermissionValidator();

  const isWaitingForParentProfiles =
    hybridReportRerun && locationState?.rerun === true && profileList == null;

  const {
    mutate: uploadResume,
    data: resumeData,
    isLoading: isLoadingResume,
  } = useUploadResume();

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target as HTMLInputElement;
    if (files && files.length > 0) {
      uploadResume(files[0]);
    }
  };
  useEffect(() => {
    if (resumeData) {
      setAliases([
        {
          firstName: resumeData.name.first,
          middleName: resumeData.name.middle,
          lastName: resumeData.name.last,
          key: uniqueUUID(),
          name_type: NameType.MAIN,
        },
      ]);
      setJobs(
        resumeData.jobs.map((job) => ({
          title: (job.job_title || "").replace(/[,.;-]/g, ""),
          company: (job.organization || "").replace(/[,.;-]/g, ""),
          startDate: job.start_date || "",
          endDate: job.end_date || "",
          country: "",
          key: uniqueUUID(),
        }))
      );
      setAddresses(
        resumeData.addresses.map((address) => ({
          street: (address.formatted || "").replace(/[,.;-]/g, ""),
          city: address.city,
          state: address.state,
          zip: "",
          country: address.country,
          key: uniqueUUID(),
        }))
      );
      setEmails(
        resumeData.emails.map((email) => ({
          email: email,
          key: uniqueUUID(),
        }))
      );

      setEducation(
        resumeData.education.map((education) => ({
          degree: education.accreditation.input_str.replace(/[,.-]/g, ""),
          institution: education.organization.replace(/[,.-]/g, ""),
          startDate: "",
          endDate: education.completion_date,
          country: education.location ? education.location.country : "",
          key: uniqueUUID(),
        }))
      );

      setSocialMedia(
        resumeData.websites.map((site) => ({ url: site, key: uniqueUUID() }))
      );
    }
  }, [resumeData]);

  useEffect(() => {
    getAllCompanies();
    getBlueprints();
  }, [getAllCompanies, getBlueprints]);

  useEffect(() => {
    if (companiesInfo && companiesInfo.length > 0) {
      const list = companiesInfo;
      const items: DropdownItem[] = list.map((element) => {
        return { id: element.external_id, label: element.name };
      });
      setCompanyItems(items);
      if (locationState?.rerun) {
        setCompanyItems([
          {
            id: locationState.parent_report.company_uuid,
            label: locationState.parent_report.company_name,
          },
        ]);
      }
    }

    if (companiesWithBlueprint && companiesWithBlueprint.length > 0) {
      const list = companiesWithBlueprint;
      const items: DropdownItem[] = list?.[0]?.blueprintsInfo.map((element) => {
        return {
          id: element.blueprint_uuid,
          label: element.name,
        };
      });
      setBlueprintItems(items);
    }

    setBehaviorKitItems(createBehaviorsKitItems(behaviorKitList));
    return () => {
      initialBluePrintCheck.current = null;
      initialBehaviorKitChange.current = null;
    };
  }, [
    companiesInfo,
    blueprintsList,
    companiesWithBlueprint,
    behaviorKitList,
    locationState,
  ]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.setBirthdate,
      payload: birthDate,
    });
  }, [birthDate]);
  useEffect(() => {
    if (resultCreatePerson) {
      // the state: reportSubmitted is for the toast message in the reports lists...
      navigate("/reports", { state: { reportSubmitted: true } });
    }
  }, [navigate, resultCreatePerson]);

  function close() {
    setShowModal(false);
  }

  const handleCompanyChange = useCallback(
    (item: DropdownItem) => {
      if (!blueprintsList) return;
      getCompaniesInfoWithBlueprint({
        company_uuids: [item.id],
        blueprintsList: blueprintsList,
      });
      setCompanyUUID(item.id);
      dispatch({ type: ActionTypes.setCompany, payload: item.id });
    },
    [blueprintsList, getCompaniesInfoWithBlueprint]
  );
  const handleBlueprintCheckChange = (item: DropdownItem) => {
    initialBluePrintCheck.current = false;
    dispatch({ type: ActionTypes.setBlueprint, payload: item.id });
  };
  const handleBehaviorKitChange = (item: DropdownItem) => {
    initialBehaviorKitChange.current = false;
    dispatch({ type: ActionTypes.setBehaviorKit, payload: item.id });
  };

  const createBehaviorsKitItems = (
    behaviorKList: BehaviorKit[] | undefined
  ) => {
    if (behaviorKList && behaviorKList.length > 0) {
      const list = behaviorKList;
      const items: DropdownItem[] = list.map((element) => {
        return {
          id: element.external_id ?? "",
          label: element.name,
        };
      });
      items.push({ id: "NULL", label: "General" });
      items.sort(dropdownItemSortEval);
      return items;
    } else {
      const emptyList: DropdownItem[] = [{ id: "NULL", label: "General" }];
      return emptyList;
    }
  };

  const showConfirmation = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setShowErrors(true);
    Object.keys(personFormError).length === 0 && setShowModal(true);
  };
  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setShowErrors(true);
    // TODO: Improve error handling so the add person button is disabled
    // if there are errors, and the endpoint call doesn't happen

    if (Object.keys(personFormError).length !== 0) {
      return;
    }

    // Hybrid rerun — PATCH parent with selected check type / behavior kit for delta vs fresh.
    if (
      hybridReportRerun &&
      locationState?.rerun === true &&
      locationState?.parent_report?.report_uuid
    ) {
      const selectedBlueprintMeta =
        blueprintsList?.find(
          (b) => b.blueprint_uuid === state.blueprint_uuid
        ) ??
        companiesWithBlueprint
          ?.find((c) => c.company_uuid === state.company_uuid)
          ?.blueprintsInfo?.find(
            (b) => b.blueprint_uuid === state.blueprint_uuid
          );
      const includeBehaviorKitInPayload =
        showBehaviorKit &&
        selectedBlueprintMeta?.report_type === REPORT_TYPES.MISCONDUCT;

      // Send form URLs (incl. newly added). EI re-merges all parent in_report URLs
      // so omissions cannot drop provided/manual profiles from the child.
      const profileUrls = profilesPrefilledFromParentRef.current
        ? state.profiles
        : undefined;

      const currentPerson = personInfoApiFromPersonFormState(state, {
        birthDate,
        aliases,
        jobs,
        emails,
        phones,
        addresses: adddresses,
        education,
      });
      const person = buildHybridRerunPersonPayload({
        current: currentPerson,
        modifiedBy: HARDCODED_ACTOR_UUID,
      });

      rerunReportRequest({
        reportUUID: locationState.parent_report.report_uuid,
        body: buildHybridRerunReportBody({
          blueprintUuid: state.blueprint_uuid,
          behaviorKitUuid: state.behavior_kit_uuid,
          includeBehaviorKitInPayload,
          profileUrls,
          person,
          customId: state.custom_id,
        }),
      })
        .then(() => {
          navigate("/reports", { state: { reportSubmitted: true } });
        })
        .catch(() => {
          // error surfaced via errorRerun -> errorMessage below
        });
      return;
    }

    const createPersonPayload: CreateReportInputApi = {
      person_info: {
        names: state.names.map((name) => {
          return {
            first_name: name.first_name.trim(),
            last_name: name.last_name.trim(),
            middle_name: name.middle_name.trim(),
            name_type: name.name_type,
          };
        }),
        jobs: state.jobs,
        addresses: state.addresses,
        birth_date: formatDateForEndpoint(state.birth_date),
        degrees: state.degrees,
        phones: state.phones.map((p) => ({
          raw_number: p.raw_number.replace(/[^0-9]/g, ""),
        })),
        emails: state.emails,
        // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
        modified_by: HARDCODED_ACTOR_UUID,
      },
      profile_urls: state.profiles
        .map((profile) => profile.trim())
        .filter((profile) => profile.length > 0),
      blueprint_uuid: state.blueprint_uuid,
      company_uuid: state.company_uuid,
      behavior_kit_uuid: state.behavior_kit_uuid,
      custom_id: state.custom_id,
      rerun_from_parent_uuid: locationState?.parent_report?.report_uuid ?? "",
    };
    if (
      createPersonPayload.behavior_kit_uuid === "NULL" ||
      createPersonPayload.behavior_kit_uuid === ""
    ) {
      delete createPersonPayload.behavior_kit_uuid;
    }
    if (!locationState?.parent_report?.report_uuid) {
      delete createPersonPayload.rerun_from_parent_uuid;
    }
    createPerson(createPersonPayload);
  };

  let defaultRerunCheckTypeLabel =
    blueprintItems.length === 1
      ? blueprintItems?.[0]
      : { id: "", label: "Select Check Type" };
  if (locationState?.rerun) {
    defaultRerunCheckTypeLabel = {
      id: locationState.parent_report.blueprint_uuid,
      label: locationState.parent_report.blueprint_name,
    };
  }

  let errorMessage = errorCreatePerson
    ? [
        `Couldn't save the report please check the field information and try again.`,
        ...errorCreatePersonData,
      ].map((p) => <div>{p}</div>)
    : errorRerun
    ? [`Couldn't rerun the report. Please try again or contact support.`].map(
        (p) => <div>{p}</div>
      )
    : "";

  return (
    <>
      <PageHeader
        title={locationState?.parent_report ? "Rerun Report" : "Add Report"}
      >
        <>
          {showResumeParse && (
            <Button
              disabled={isLoadingResume}
              type={ButtonTypes.default}
              text={isLoadingResume ? "Parsing..." : "Parse Resume"}
              colorType={ButtonColorTypes.success}
              onClick={() =>
                fileInput && fileInput.current && fileInput.current.click()
              }
            />
          )}
          {canBulkUploadReports && (
            <Link className="text-blue-400" to="/reports/bulkupload">
              Bulk Upload
            </Link>
          )}
        </>
      </PageHeader>
      <Container>
        <form>
          <FormTwoColumnContainer title="Screening Company">
            <div className="flex items-center">
              <div className="w-96">
                <DropdownCombo
                  items={companyItems}
                  initial={locationState?.rerun ? companyItems[0] : undefined}
                  disabled={isLoadingCompanies || locationState?.rerun}
                  onChange={handleCompanyChange}
                  needTypeAhead={!locationState?.rerun}
                  label={"Select Company*"}
                  placeHolder={
                    isLoadingCompanies ? "Loading" : "Select Company"
                  }
                  name="selected-company"
                  {...(showErrors && {
                    isValid: personFormError.company ? false : true,
                  })}
                ></DropdownCombo>
              </div>
            </div>
          </FormTwoColumnContainer>
          {/* Blueprint Section */}
          <FormTwoColumnContainer title="Check Type">
            <div className="flex items-center">
              <div className="w-96">
                {(blueprintItems.length === 0 ||
                  isLoadingBlueprints ||
                  isLoadingCompanyBlueprints) && (
                  <DropdownCombo
                    items={[]}
                    disabled={true}
                    placeHolder={
                      isLoadingBlueprints || isLoadingCompanyBlueprints
                        ? "Loading"
                        : "Please Select Company First"
                    }
                    label={"Select Check Type*"}
                    name="selected-check-type"
                  ></DropdownCombo>
                )}
                {blueprintItems.length !== 0 && (
                  <DropdownCombo
                    containerClassName={classNames(
                      isLoadingBlueprints || isLoadingCompanyBlueprints
                        ? "hidden"
                        : ""
                    )}
                    items={blueprintItems}
                    {...(initialBluePrintCheck.current === null && {
                      initial: defaultRerunCheckTypeLabel,
                    })}
                    label={"Select Check Type*"}
                    name="selected-check-type"
                    {...(showErrors && {
                      isValid: personFormError.blueprint_uuid ? false : true,
                    })}
                    onChange={handleBlueprintCheckChange}
                  ></DropdownCombo>
                )}
                {behaviorKitItems && showBehaviorKit && (
                  <DropdownCombo
                    containerClassName="mt-5"
                    items={behaviorKitItems}
                    {...(initialBehaviorKitChange.current === null && {
                      initial:
                        locationState?.rerun &&
                        locationState.parent_report.behavior_kit_uuid &&
                        locationState.parent_report.behavior_kit_name
                          ? {
                              id: locationState.parent_report.behavior_kit_uuid,
                              label:
                                locationState.parent_report.behavior_kit_name,
                            }
                          : { id: "NULL", label: "General" },
                    })}
                    onChange={handleBehaviorKitChange}
                    label={"Select Behavior Kit*"}
                    name="selected-behavior-kit"
                    icon={
                      <InformationCircleIcon
                        height={24}
                        width={24}
                        color="grey"
                      />
                    }
                    tooltip="Behavior kits are the configurable filters that companies can use to set behavior flags, lookback periods, and keywords for any given check type.
                    The General Behavior Kit utilizes configured settings derived from the selected Check Type."
                  ></DropdownCombo>
                )}
              </div>
            </div>
          </FormTwoColumnContainer>
          {/* Alias Section */}
          <FormTwoColumnContainer title="Personal Info">
            <PersonFormAliases
              {...(aliases && { prefill: aliases })}
              onChange={(aliases) =>
                dispatch({ type: ActionTypes.setNames, payload: aliases })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* Emails Section */}
          <FormTwoColumnContainer className="mt-12">
            <PersonFormEmails
              {...(emails && { prefill: emails })}
              onChange={(emails) =>
                dispatch({ type: ActionTypes.setEmails, payload: emails })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* Phones Section */}
          <FormTwoColumnContainer className="mt-12">
            <PersonFormPhones
              {...(phones && { prefill: phones })}
              onChange={(phones) =>
                dispatch({ type: ActionTypes.setPhones, payload: phones })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* BirthDate Section */}
          <FormTwoColumnContainer className="mt-12">
            <InputField
              value={birthDate}
              handleChange={(e) => {
                setBirthDate(e.target.value);
              }}
              name={"birth-date"}
              placeholder=""
              label={"Birth Date"}
              type="date"
              max={new Date().toISOString().split("T")[0]}
              {...(showErrors && {
                className: personFormError?.birth_date ? "border-rose-600" : "",
              })}
            />
          </FormTwoColumnContainer>
          {/* Address Section */}
          <FormTwoColumnContainer title="Address" className="mt-12">
            <PersonFormAddresses
              {...(adddresses && { prefill: adddresses })}
              onChange={(addresses) =>
                dispatch({ type: ActionTypes.setAddresses, payload: addresses })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* Education Section */}
          <FormTwoColumnContainer title="Education">
            <PersonFormEducation
              {...(education && { prefill: education })}
              onChange={(education) =>
                dispatch({ type: ActionTypes.setDegrees, payload: education })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* Job Section */}
          <FormTwoColumnContainer title="Job">
            <PersonFormJob
              {...(jobs && { prefill: jobs })}
              onChange={(jobs) =>
                dispatch({ type: ActionTypes.setJobs, payload: jobs })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>
          {/* Social Networks Section */}
          <FormTwoColumnContainer title="Social Media Profiles">
            <PersonFormSocial
              {...(socialMedia && { prefill: socialMedia })}
              onChange={(social) =>
                dispatch({ type: ActionTypes.setProfiles, payload: social })
              }
              {...(showErrors && { error: personFormError })}
            />
          </FormTwoColumnContainer>

          {/* Client Reference */}
          <FormTwoColumnContainer title="Client Reference ID">
            <InputField
              handleChange={(e) => {
                dispatch({
                  type: ActionTypes.setCustomID,
                  payload: e.target.value,
                });
              }}
              name={"reference-id"}
              placeholder=""
              defaultValue={
                locationState?.rerun
                  ? locationState?.parent_report?.client_report_id
                  : ""
              }
            />
          </FormTwoColumnContainer>
          <Divider addMargin={false} />
          <PersonFormErrorSummary
            formState={state}
            handleError={setPersonFormError}
            showErrors={showErrors}
          />
          <FormTwoColumnContainer>
            <div className="mt-6">
              <Button
                disabled={
                  isLoadingCreatePerson ||
                  isLoadingRerun ||
                  isWaitingForParentProfiles
                }
                type={ButtonTypes.add}
                text={
                  isLoadingCreatePerson || isLoadingRerun ? (
                    <LoadingSpinner />
                  ) : (
                    "Add Report"
                  )
                }
                colorType={ButtonColorTypes.success}
                onClick={showConfirmation}
              />
            </div>
          </FormTwoColumnContainer>
          <input
            className="hidden"
            type="file"
            ref={fileInput}
            name="_file"
            onChange={changeHandler}
          />
        </form>
      </Container>
      <Modal open={showModal} width="summary--customSize" onClose={close}>
        <PersonFormReview
          isLoadingCreatePerson={isLoadingCreatePerson || isLoadingRerun}
          close={close}
          behaviorKitItems={behaviorKitItems}
          companyItems={companyItems}
          blueprintItems={blueprintItems}
          state={state}
          handleSubmit={handleSubmit}
          error={errorMessage}
        />
      </Modal>
    </>
  );
};

export default PersonForm;
